// VLibras: widget oficial e gratuito do Governo Federal que traduz o texto da
// página para Libras com um avatar 3D (https://vlibras.gov.br).
//
// Integração oficial (vlibras.gov.br/doc/widget/installation/webpageintegration.html):
//   <script src="https://vlibras.gov.br/app/vlibras-plugin.js"></script>
//   <script>new window.VLibras.Widget('https://vlibras.gov.br/app');</script>
// O script só de carregador (vlibras-plugin.js) já ativa o widget. Ele põe no
// <body> o botão (div#vlibras-access-wrapper, com shadow DOM) e, no primeiro
// clique, carrega o app (módulo vlibras-plugin-app.js, que cria
// div#vlibras-app-root). Os arquivos vêm de vlibras.gov.br/app, que redireciona
// para cdn.jsdelivr.net; a tradução e o dicionário de sinais vêm de
// *.vlibras.gov.br. CSP: nginx/seguranca/csp-plataforma.conf.
//
// Aqui só se carrega quando a opção está ligada E o idioma é português (o
// VLibras traduz apenas português). Quem não usa não baixa nada.

export const VLIBRAS_ROOT = 'https://vlibras.gov.br/app';
export const VLIBRAS_SCRIPT = `${VLIBRAS_ROOT}/vlibras-plugin.js`;
const ID_SCRIPT = 'vlibras-plugin-script';
// Elementos que o widget cria: o botão e o app no <body>; o estilo da captura
// de texto (contorno ao passar o mouse) no <head>, só enquanto o avatar está
// aberto. Conferido no VLibras 7.12.2 (29/10/2026).
const SELETORES_DO_WIDGET = ['#vlibras-access-wrapper', '#vlibras-app-root', 'style[id^="@text-capture"]'];
// O widget guarda no localStorage se o avatar estava aberto e, se estava,
// reabre o avatar sozinho em toda página carregada (inclusive no login).
export const CHAVE_ESTADO_WIDGET = '@vlibras-widget';

// Desligar tira o widget da página, mas o navegador não executa de novo um
// módulo já carregado (o app). Então os nós saem do DOM e ficam guardados
// aqui (com o pai de cada um); religar devolve os mesmos nós, sem baixar nada
// outra vez.
let guardados = [];
let carregando = null;
// Desligado enquanto o script ainda chegava: o widget sai assim que nascer.
let desejado = false;
// Enquanto desligado, tira o que o widget puser de volta na página (o app
// continua vivo na memória e pode reanexar o botão ou o avatar).
let observador = null;

export const idiomaAceitaVLibras = (idioma) => String(idioma || '').toLowerCase().startsWith('pt');

const noDocumento = () => SELETORES_DO_WIDGET
  .map((seletor) => document.querySelector(seletor))
  .filter(Boolean);

export const vlibrasNaPagina = () => Boolean(
  document.querySelector('#vlibras-access-wrapper') || document.querySelector('#vlibras-app-root')
);

// Botão de abrir do VLibras (dentro do shadow DOM do wrapper), para o botão
// "Ocultar Libras" se posicionar ao lado dele.
export const botaoDoVLibras = () => {
  const wrapper = typeof document !== 'undefined' && document.getElementById('vlibras-access-wrapper');
  if (!wrapper) return null;
  const raiz = wrapper.shadowRoot || wrapper;
  return raiz.querySelector('#vlibras-button') || raiz.querySelector('button');
};

// Avatar aberto? Fecha pelo próprio botão do widget, para que o estado dele
// (isOpen) fique "fechado" e religar mostre só o botão. E, por garantia, o
// estado gravado também passa a "fechado": a próxima página não reabre.
const fecharAvatar = () => {
  const app = document.getElementById('vlibras-app-root');
  const raiz = app && (app.shadowRoot || app);
  const fechar = raiz && [...raiz.querySelectorAll('button')]
    .find((b) => /^(fechar|close)$/i.test(b.getAttribute('aria-label') || ''));
  try { fechar?.click(); } catch (_err) { /* melhor esforço */ }
};

const esquecerAvatarAberto = () => {
  try {
    const texto = window.localStorage.getItem(CHAVE_ESTADO_WIDGET);
    if (!texto) return;
    const salvo = JSON.parse(texto);
    if (salvo?.state?.isOpen) {
      salvo.state.isOpen = false;
      window.localStorage.setItem(CHAVE_ESTADO_WIDGET, JSON.stringify(salvo));
    }
  } catch (_err) { /* localStorage indisponível ou formato novo: ignora */ }
};

const guardarNos = (nos) => {
  nos.forEach((no) => {
    const pai = no.parentNode;
    no.remove();
    guardados = guardados.filter((g) => g.no !== no);
    guardados.push({ no, pai });
  });
};

const vigiar = () => {
  if (observador || typeof MutationObserver === 'undefined') return;
  observador = new MutationObserver(() => {
    if (desejado || !globalThis.document) return;
    const voltaram = noDocumento();
    if (voltaram.length) guardarNos(voltaram);
  });
  // O widget só põe filhos diretos no <body> e no <head>: basta olhar ali.
  observador.observe(document.body, { childList: true });
  if (document.head) observador.observe(document.head, { childList: true });
};

const pararDeVigiar = () => {
  observador?.disconnect();
  observador = null;
};

export const carregarVLibras = () => {
  if (typeof document === 'undefined') return Promise.resolve(false);
  desejado = true;
  pararDeVigiar();

  if (guardados.length) {
    guardados.forEach(({ no, pai }) => (pai && pai.isConnected ? pai : document.body).appendChild(no));
    guardados = [];
    return Promise.resolve(true);
  }

  if (vlibrasNaPagina() || document.getElementById(ID_SCRIPT)) return carregando || Promise.resolve(true);

  carregando = new Promise((resolve) => {
    const script = document.createElement('script');
    script.id = ID_SCRIPT;
    script.src = VLIBRAS_SCRIPT;
    script.async = true;
    script.onload = () => {
      try {
        // Posição padrão do widget: à direita, no meio da altura da tela
        // (longe do chat e dos botões do canto inferior direito).
        if (window.VLibras?.Widget) new window.VLibras.Widget(VLIBRAS_ROOT);
      } catch (_err) { /* o carregador já ativa o widget sozinho */ }
      if (!desejado) removerVLibras();
      resolve(true);
    };
    script.onerror = () => {
      script.remove();
      carregando = null;
      resolve(false);
    };
    document.body.appendChild(script);
  });
  return carregando;
};

export const removerVLibras = () => {
  if (typeof document === 'undefined') return;
  desejado = false;
  esquecerAvatarAberto();
  // Nada foi baixado nesta página: não há o que tirar nem vigiar.
  if (!document.getElementById(ID_SCRIPT) && !guardados.length) return;
  fecharAvatar();
  guardarNos(noDocumento());
  esquecerAvatarAberto();
  vigiar();
};

// Só para os testes: volta ao estado de página recém-aberta.
export const __reiniciarVLibrasParaTeste = () => {
  pararDeVigiar();
  guardados = [];
  carregando = null;
  desejado = false;
  document.getElementById(ID_SCRIPT)?.remove();
  noDocumento().forEach((no) => no.remove());
};
